import CardCommon from '@/pages/opinion/components/CardCommon';
import RegionCountryTable from '../../components/RegionCountryTable';
import React, { useMemo, useState } from 'react';
import TopFilterInfo from './TopFilterInfo';
import { Button } from 'tea-component';
import {
  arrayToObj,
  dateTypeMap,
  getRationPercentValue,
  getTimeSpaceArray,
} from '@/pages/opinion/utils';
import { formatDateFunc, getQOQText } from '@/pages/opinion/utils/time';
import { dateFormatMap } from '@/pages/opinion/constants';
import { ParamsTimeType } from '@/pages/opinion/types/common';
import useRegionInfo from '../../hooks/useRegionInfo';
import { getLocale, useIntl } from 'umi';
import { downloadAction } from '@/pages/opinion/components/DownloadCommon/download-page';
import UserGuideGameStore from '@op/components/UserGuide/game-store';
import { sentimentFormat } from '@/pages/opinion/utils/value';
import './index.scss';
import { openWindowUrl } from '@op/utils/url';
import { languageMapDocumentUrl } from '@op/components/BubbleCommon/copywritingKey/utils';
import useRegionReviews from '../../hooks/useRegionReviews';

export default function ByRegionOrCountry({
  originRegion,
  keepParams,
  userScoreTable,
  trendsTimeline,
  gameInfo,
  loading,
}: any) {
  const [tableSelectable, setTableSelectable] = useState(false);
  const { formatMessage } = useIntl();
  const { reviewsResultMap } = useRegionReviews({ gameInfo, keepParams });

  const regionTableData = useMemo(() => {
    if (
      !keepParams &&
      userScoreTable.length === 0 &&
      Object.keys(trendsTimeline || {}).length === 0
    ) {
      return [];
    }
    const { date_type, start_time, end_time } = keepParams;

    const timeType: any = dateTypeMap[date_type as ParamsTimeType];
    const formatDate = formatDateFunc(date_type);

    function getTimeLineInfo(timelineMap: any, key: string) {
      const timeLine = timelineMap[key] || [];
      const { timeline_of_start_time, timeline_of_end_time } = timelineMap;

      const timeLineMap = arrayToObj(timeLine, ({ time }) => formatDate(time));

      const dateArray = getTimeSpaceArray(
        timeline_of_start_time,
        timeline_of_end_time,
        timeType,
        dateFormatMap[timeType],
      );
      const scoreLineData: any[] = [];
      const reviewsLineData: any[] = [];
      dateArray.map((key) => {
        const { mentions, score } = timeLineMap[key] || {};
        scoreLineData.push(score);
        reviewsLineData.push(mentions);
      });

      return {
        x: dateArray,
        score: scoreLineData,
        reviews: reviewsLineData,
      };
    }

    function getVsData(
      timeLine: { x: Array<string>; score: Array<number> },
      isAppleSelected = false,
      key: string,
    ) {
      const { x, score } = timeLine;
      const { isSingleDate, isSingleDay } = getQOQText(
        date_type,
        start_time,
        end_time,
      );
      let oldIndex = 0;
      let newIndex = score.length - 1;

      if (isSingleDate) {
        oldIndex = newIndex - 1;
      }
      // 如果单天并是apple selected 就在推前一天
      if (isAppleSelected && isSingleDay) {
        newIndex = newIndex - 1;
        oldIndex = oldIndex - 1;
      }
      const newScore = sentimentFormat(score[newIndex], 0) as number;
      const oldScore = sentimentFormat(score[oldIndex], 0) as number;

      return {
        [`${key}_oldTime`]: [x[oldIndex]],
        [`${key}_newTime`]: [x[newIndex]],
        [`${key}_newScore`]: newScore,
        [`${key}_oldScore`]: oldScore,
        [`${key}_rationPercent`]: getRationPercentValue(newScore, oldScore), // 环比
        [`${key}_compareScore`]: Math.abs(newScore - oldScore), // 作为比较过滤值
      };
    }

    const tableData = userScoreTable.map(
      ({ region, ...data }: { [x: string]: any }) => {
        const result: any = {};
        const timelineMap = trendsTimeline[region] || {};
        for (const key in data) {
          const { score, mentions } = data[key];
          const timeLine = getTimeLineInfo(timelineMap, key);

          Object.assign(result, {
            [key + '_timeLine']: timeLine,
            [key + '_score']: score,
            [key + '_reviews']: mentions,
            [key + '_flag']: score < (key.includes('lifetime') ? 4 : 3.5),
            ...getVsData(timeLine, key === 'app store-selected', key),
          });
        }
        const info = Object.assign(
          {
            'app store-lifetime_reviews': 0,
            'google play-lifetime_reviews': 0,
          },
          reviewsResultMap[region] || {},
        );
        return { ...result, region, ...info };
      },
    );
    return tableData;
  }, [userScoreTable, trendsTimeline, reviewsResultMap]);

  const {
    useFilterRegion,
    setUseFilterRegion,
    regionOptions,
    selectValue,
    regionChange,
    filterCountries,
  } = useRegionInfo({ originRegion, gameInfo });
  const [scoreFilterValue, setScoreFilterValue] = useState(null);
  const scoreFilterChange = (value: any) => {
    setScoreFilterValue(value);
  };

  function getTipsText() {
    if (getLocale() === 'en-US')
      return (
        <>
          Due to platform restrictions, country data from Google Play are{' '}
          <Button
            type="link"
            style={{ marginRight: 0, lineHeight: '14px' }}
            onClick={() => openWindowUrl(languageMapDocumentUrl)}
          >
            mapped by language
          </Button>{' '}
          (except for Lifetime Ratings), whereas data from App Store are mapped
          by countries.
        </>
      );

    return (
      <>
        由于平台限制，来自Google Play的
        <Button
          style={{ marginRight: 0, lineHeight: '14px' }}
          onClick={() => openWindowUrl(languageMapDocumentUrl)}
          type="link"
        >
          国家数据按照语种进行划分
        </Button>
        (累计评分除外)，而App Store的数据则是按照国家来划分的。
      </>
    );
  }

  return (
    <CardCommon
      loadingHeight={600}
      loading={loading}
      title={
        <div className="card-common-title">
          <label className="title-info">
            {formatMessage({ id: 'By Region/Country' })}
          </label>
          <div className="tips-info-box">{getTipsText()}</div>
        </div>
      }
      hasDownload
      className="by-region-country-box"
      handleDownloadOnClick={() => {
        const downLoadParams = {
          ...keepParams,
          group_bys: ['channel', 'country'],
          metrics: ['user_score'],
        };

        downloadAction({
          simpleName: 'UserScore',
          sheetName: 'Key Metrics',
          downLoadParams,
          align: 'center',
          downloadFunc: 'downloadKeyMetrics',
        });
      }}
    >
      <>
        <UserGuideGameStore></UserGuideGameStore>
        <TopFilterInfo
          tableSelectable={tableSelectable}
          setTableSelectable={setTableSelectable}
          regionOptions={regionOptions}
          scoreFilterChange={scoreFilterChange}
          regionValue={selectValue}
          useFilterRegion={useFilterRegion}
          setUseFilterRegion={setUseFilterRegion}
          setRegionValue={regionChange}
        ></TopFilterInfo>
        <RegionCountryTable
          dataList={regionTableData}
          selectable={tableSelectable}
          filterCountries={filterCountries}
          scoreFilterValue={scoreFilterValue}
          params={keepParams}
          dateType={keepParams?.date_type}
        />
      </>
    </CardCommon>
  );
}
